import React, { useMemo, useState } from "react";
import { useForm, Controller, useWatch } from "react-hook-form";
import { FiTrash2 } from "react-icons/fi";
import SearchSelect from "components/ui/SearchSelect";
import AudienceSelect, {
  toAudienceValue,
  fromAudienceValue,
  audienceRules,
} from "components/products/AudienceSelect";
import {
  useGetOrganizationOfficesQuery,
  useGetMatchOptionsQuery,
} from "apis/packageRule";
import {
  formatPropertyType,
  formatProductCategory,
} from "utils/helperFunctions";
import { describeOwner } from "components/packageRule/View";

// Package rule form — what the rule matches on, and what it recommends.
//
// Products and packages are picked from the live catalogue, so the rule stores
// ids only; prices and office entitlements are resolved when an agent opens a
// property. That's why nothing here asks for a price.

const errorBorderClass = (fieldError) => (fieldError ? "border-red-500" : "");


const bedroomOptionsFor = (kind) => {
  const opts = [
    {
      value: "",
      label: kind === "min" ? "No minimum" : "No maximum",
    },
  ];
  for (let i = 0; i <= 10; i += 1) {
    opts.push({ value: String(i), label: String(i) });
  }
  return opts;
};

const MIN_BEDROOM_OPTIONS = bedroomOptionsFor("min");
const MAX_BEDROOM_OPTIONS = bedroomOptionsFor("max");

export default function PackageRuleEdit({
  initialData = {},
  organizations = [],
  products = [],
  packages = [],
  isSaving = false,
  onSubmit,
  onCancel,
}) {
  const {
    register,
    control,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm({
    defaultValues: {
      organizationId: String(
        initialData?.organizationId?._id || initialData?.organizationId || "",
      ),
      name: initialData?.name || "",
      audience: toAudienceValue(initialData?.offices),
      propertyTypes: initialData?.propertyTypes || [],
      finishes: initialData?.finishes || [],
      minBedrooms:
        initialData?.minBedrooms == null ? "" : String(initialData.minBedrooms),
      maxBedrooms:
        initialData?.maxBedrooms == null ? "" : String(initialData.maxBedrooms),
      productIds: (initialData?.productIds || []).map((p) =>
        String(p?._id || p),
      ),
      quantities: { ...(initialData?.quantities || {}) },
      packageIds: (initialData?.packageIds || []).map((p) =>
        String(p?._id || p),
      ),
    },
  });

  const selectedProductIds = useWatch({ control, name: "productIds" }) || [];
  const selectedPackageIds = useWatch({ control, name: "packageIds" }) || [];
  const quantities = useWatch({ control, name: "quantities" }) || {};
  const minBedrooms = useWatch({ control, name: "minBedrooms" });
  const maxBedrooms = useWatch({ control, name: "maxBedrooms" });
  const organizationId = useWatch({ control, name: "organizationId" });

  // Offices follow the chosen organization, so a rule can never be scoped to
  // another organization's office.
  const { data: offices = [] } = useGetOrganizationOfficesQuery(organizationId);

  const organizationOptions = (organizations || []).map((o) => ({
    value: String(o._id),
    label: o.name,
    subLabel: o.slug,
  }));

  const isExisting = !!initialData?._id;
  const owner = describeOwner(initialData || {});
  const isOwnPackage = owner.kind !== "group";

  // An agent's or company's package can name that company's own products,
  // which the platform catalogue doesn't list — fall back to the rule's copy.
  const productById = useMemo(() => {
    const byId = new Map();
    (initialData?.productIds || []).forEach((p) => {
      if (p?._id) byId.set(String(p._id), p);
    });
    (products || []).forEach((p) => byId.set(String(p._id), p));
    return byId;
  }, [initialData, products]);

  const { data: matchOptions } = useGetMatchOptionsQuery();
  const propertyTypeOptions = (matchOptions.propertyTypes || []).map((t) => ({
    value: t,
    label: formatPropertyType(t),
  }));
  const finishOptions = (matchOptions.finishes || []).map((f) => ({
    value: f,
    label: f,
  }));

  const toOption = (p) => ({
    value: String(p._id),
    label: p.name,
    subLabel: [
      p.price != null ? `$${p.price}` : null,
      p.contractorCompanyId?.name || "in-house",
    ]
      .filter(Boolean)
      .join(" · "),
  });

  // Products grouped by category, the way the agent's marketing cart presents
  // them — picking a photo package means opening "Photo", not scrolling one
  // flat list of every product on the platform.
  const productsByCategory = useMemo(() => {
    const groups = new Map();
    (products || []).forEach((p) => {
      const key = p.category || "other";
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(p);
    });
    return [...groups.entries()]
      .map(([category, items]) => ({
        category,
        items: items.sort((a, b) => (a.price || 0) - (b.price || 0)),
      }))
      .sort((a, b) => a.category.localeCompare(b.category));
  }, [products]);

  const [activeCategory, setActiveCategory] = useState(null);

  const packageOptions = (packages || []).map((p) => ({
    value: String(p._id),
    label: p.name,
    subLabel: [
      p.price != null ? `$${p.price}` : null,
      p.contractorCompanyId?.name || "in-house",
    ]
      .filter(Boolean)
      .join(" · "),
  }));

  const nothingSelected =
    selectedProductIds.length + selectedPackageIds.length === 0;

  const boundsInverted =
    minBedrooms !== "" &&
    maxBedrooms !== "" &&
    Number(minBedrooms) > Number(maxBedrooms);

  const submit = (values) => {
    if (nothingSelected || boundsInverted) return;
    const quantities = Object.fromEntries(
      values.productIds
        .map((id) => [id, Number(values.quantities?.[id])])
        .filter(([, quantity]) => Number.isInteger(quantity) && quantity > 1),
    );
    onSubmit({
      organizationId: values.organizationId,
      name: values.name.trim(),
      ...(isOwnPackage ? {} : { offices: fromAudienceValue(values.audience) }),
      propertyTypes: values.propertyTypes || [],
      finishes: values.finishes || [],
      minBedrooms: values.minBedrooms === "" ? null : Number(values.minBedrooms),
      maxBedrooms: values.maxBedrooms === "" ? null : Number(values.maxBedrooms),
      productIds: values.productIds,
      quantities,
      packageIds: values.packageIds,
    });
  };

  return (
    <form onSubmit={handleSubmit(submit)} className="space-y-6">
      {/* ─── Organization ─── */}
      <div className="space-y-2">
        <label className="form-label">Organization *</label>
        <Controller
          name="organizationId"
          control={control}
          rules={{ required: "Organization is required" }}
          render={({ field }) => (
            <SearchSelect
              options={organizationOptions}
              value={
                organizationOptions.find((o) => o.value === field.value) || null
              }
              onChange={(option) => {
                field.onChange(option?.value || "");
                // Offices belong to the previous organization — clear them so
                // a rule can't keep a foreign office after switching.
                setValue("audience", { mode: "all", offices: [] });
              }}
              placeholder="Select an organization"
              // Set once, on create. The backend never reassigns a rule's
              // organization, so offering it on edit would be a no-op.
              disabled={isExisting}
              className={errorBorderClass(errors.organizationId)}
            />
          )}
        />
        {errors.organizationId ? (
          <p className="form-error-message">{errors.organizationId.message}</p>
        ) : (
          isExisting && (
            <p className="text-sm text-darkgray">
              A rule can't be moved between organizations.
            </p>
          )
        )}
      </div>

      {isOwnPackage && (
        <div className="space-y-1">
          <label className="form-label">Made By</label>
          <p className="text-sm">{owner.label}</p>
          <p className="text-sm text-darkgray">
            Made in the app. It's used before the group's rules for this{" "}
            {owner.kind === "agent" ? "agent's" : "company's"} listings.
          </p>
        </div>
      )}

      {/* ─── Rule name ─── */}
      <div className="space-y-2">
        <label htmlFor="name" className="form-label">
          Rule Name *
        </label>
        <input
          id="name"
          type="text"
          placeholder="e.g. 3 Bedroom, 5+ Bedroom Luxury"
          {...register("name", { required: "Rule name is required" })}
          className={`form-input border ${errorBorderClass(errors.name)}`}
        />
        {errors.name ? (
          <p className="form-error-message">{errors.name.message}</p>
        ) : (
          <p className="text-sm text-darkgray">
            Shown to the agent as the recommended package's name.
          </p>
        )}
      </div>

      {/* ─── Match on ─── */}
      <div className="border-t border-mediumgray pt-4">
        <h5 className="font-medium mb-1">Match On</h5>
        <p className="text-sm text-darkgray mb-4">
          A property must satisfy every condition below. Leave a condition blank
          to ignore that attribute.
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-2">
            <label className="form-label">Min Bedrooms</label>
            <Controller
              name="minBedrooms"
              control={control}
              render={({ field }) => (
                <SearchSelect
                  options={MIN_BEDROOM_OPTIONS}
                  value={
                    MIN_BEDROOM_OPTIONS.find((o) => o.value === field.value) ||
                    MIN_BEDROOM_OPTIONS[0]
                  }
                  onChange={(option) => field.onChange(option?.value ?? "")}
                  searchable={false}
                />
              )}
            />
          </div>

          <div className="space-y-2">
            <label className="form-label">Max Bedrooms</label>
            <Controller
              name="maxBedrooms"
              control={control}
              render={({ field }) => (
                <SearchSelect
                  options={MAX_BEDROOM_OPTIONS}
                  value={
                    MAX_BEDROOM_OPTIONS.find((o) => o.value === field.value) ||
                    MAX_BEDROOM_OPTIONS[0]
                  }
                  onChange={(option) => field.onChange(option?.value ?? "")}
                  searchable={false}
                />
              )}
            />
          </div>
        </div>

        {boundsInverted && (
          <p className="form-error-message mt-2">
            Min bedrooms cannot be greater than max bedrooms.
          </p>
        )}

        <div className="space-y-2 mt-4">
          <label className="form-label">Property Types</label>
          <Controller
            name="propertyTypes"
            control={control}
            render={({ field }) => (
              <SearchSelect
                options={propertyTypeOptions}
                value={propertyTypeOptions.filter((o) =>
                  (field.value || []).includes(o.value),
                )}
                onChange={(opts) =>
                  field.onChange((opts || []).map((o) => o.value))
                }
                placeholder="Any property type"
                multiple
              />
            )}
          />
          <p className="text-sm text-darkgray">
            Matches when the property is any one of these. Leave empty for any
            property type.
          </p>
        </div>

        <div className="space-y-2 mt-4">
          <label className="form-label">Finishes</label>
          <Controller
            name="finishes"
            control={control}
            render={({ field }) => (
              <SearchSelect
                options={finishOptions}
                value={finishOptions.filter((o) =>
                  (field.value || []).includes(o.value),
                )}
                onChange={(opts) =>
                  field.onChange((opts || []).map((o) => o.value))
                }
                placeholder="Any finish"
                searchable={false}
                multiple
              />
            )}
          />
          <p className="text-sm text-darkgray">
            Matches when the property's finish is any one of these. Leave empty
            for any finish.
          </p>
        </div>
      </div>

      {/* ─── Recommends ─── */}
      <div className="border-t border-mediumgray pt-4">
        <h5 className="font-medium mb-1">Recommends</h5>
        <p className="text-sm text-darkgray mb-4">
          Shown in the order picked. Prices always come from the catalogue, and
          anything the office isn't entitled to is dropped at request time.
        </p>

        {/* Products, by category — same shape as the agent's marketing cart:
            pick a category tab, then the items inside it. */}
        <Controller
          name="productIds"
          control={control}
          render={({ field }) => {
            const selected = field.value || [];
            const toggle = (id) =>
              field.onChange(
                selected.includes(id)
                  ? selected.filter((s) => s !== id)
                  : [...selected, id],
              );

            const openItems =
              productsByCategory.find((c) => c.category === activeCategory)
                ?.items || [];

            return (
              <div className="space-y-3">
                <div className="flex items-center justify-between gap-2">
                  <label className="form-label mb-0">Products</label>
                  {selected.length > 0 && (
                    <button
                      type="button"
                      onClick={() => field.onChange([])}
                      className="text-sm text-darkgray hover:text-black underline"
                    >
                      Clear all
                    </button>
                  )}
                </div>

                {/* What's picked — an ordered list, not a wall of pills. The
                    number is the order the agent sees them in. */}
                {selected.length > 0 && (
                  <ol className="border border-mediumgray rounded-lg divide-y divide-mediumgray overflow-hidden">
                    {selected.map((id, i) => {
                      const product = productById.get(id);
                      return (
                        <li
                          key={id}
                          className="flex items-center gap-3 px-3 py-2 bg-lightergray"
                        >
                          <span className="flex-shrink-0 w-6 h-6 rounded-full bg-black text-white text-xs flex items-center justify-center">
                            {i + 1}
                          </span>
                          <span className="min-w-0 flex-1">
                            <span className="block text-sm font-medium truncate">
                              {product?.name || id}
                            </span>
                            <span className="block text-xs text-darkgray truncate">
                              {product
                                ? [
                                    formatProductCategory(product.category),
                                    product.price != null
                                      ? `$${product.price}`
                                      : null,
                                  ]
                                    .filter(Boolean)
                                    .join(" · ")
                                : ""}
                            </span>
                          </span>
                          <input
                            type="number"
                            min="1"
                            step="1"
                            value={quantities[id] ?? 1}
                            onChange={(e) =>
                              setValue("quantities", {
                                ...quantities,
                                [id]: e.target.value,
                              })
                            }
                            className="form-input border w-16 flex-shrink-0 text-sm"
                            title="Quantity"
                            aria-label={`Quantity of ${product?.name || "product"}`}
                          />
                          <button
                            type="button"
                            onClick={() => toggle(id)}
                            className="flex-shrink-0 delete-icon"
                            title="Remove"
                          >
                            <FiTrash2 size={16} />
                          </button>
                        </li>
                      );
                    })}
                  </ol>
                )}

                {/* Category picker — a select, so 14 categories don't become a
                    wall of buttons on a phone. */}
                <SearchSelect
                  options={productsByCategory.map(({ category, items }) => {
                    const count = items.filter((p) =>
                      selected.includes(String(p._id)),
                    ).length;
                    return {
                      value: category,
                      label: formatProductCategory(category),
                      subLabel: `${items.length} product${items.length === 1 ? "" : "s"}${
                        count ? ` · ${count} selected` : ""
                      }`,
                    };
                  })}
                  value={
                    activeCategory
                      ? {
                          value: activeCategory,
                          label: formatProductCategory(activeCategory),
                        }
                      : null
                  }
                  onChange={(option) => setActiveCategory(option?.value || null)}
                  placeholder="Browse a category to add products..."
                  isClearable
                />

                {activeCategory && (
                  <div className="border border-mediumgray rounded-lg divide-y divide-mediumgray max-h-72 overflow-y-auto">
                    {openItems.map((p) => {
                      const id = String(p._id);
                      const isOn = selected.includes(id);
                      return (
                        <label
                          key={id}
                          className={`flex items-center gap-3 px-3 py-3 cursor-pointer transition-colors ${
                            isOn ? "bg-lightergray" : "hover:bg-lightergray"
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={isOn}
                            onChange={() => toggle(id)}
                            className="flex-shrink-0 w-4 h-4"
                          />
                          <span className="min-w-0 flex-1">
                            <span className="block text-sm font-medium">
                              {p.name}
                            </span>
                            <span className="block text-xs text-darkgray">
                              {toOption(p).subLabel}
                            </span>
                          </span>
                        </label>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          }}
        />

        <div className="space-y-2 mt-4">
          <label className="form-label">Packages</label>
          <Controller
            name="packageIds"
            control={control}
            render={({ field }) => (
              <SearchSelect
                options={packageOptions}
                value={packageOptions.filter((o) =>
                  (field.value || []).includes(o.value),
                )}
                onChange={(opts) =>
                  field.onChange((opts || []).map((o) => o.value))
                }
                placeholder={
                  packageOptions.length
                    ? "Search packages..."
                    : "No packages available"
                }
                disabled={!packageOptions.length}
                multiple
              />
            )}
          />
        </div>

        {nothingSelected && (
          <p className="form-error-message mt-2">
            Select at least one product or package.
          </p>
        )}
      </div>

      {/* ─── Available to ─── */}
      {!isOwnPackage && (
        <div className="border-t border-mediumgray pt-4">
          <h5 className="font-medium mb-1">Available To</h5>
          <p className="text-sm text-darkgray mb-4">
            An office-specific rule always wins over an organization-wide one,
            so one office can override a single tier without restating the
            ladder.
          </p>
          <Controller
            name="audience"
            control={control}
            rules={audienceRules}
            render={({ field }) => (
              <AudienceSelect
                value={field.value}
                onChange={field.onChange}
                error={errors.audience?.message}
                offices={offices}
              />
            )}
          />
        </div>
      )}

      <div className="flex gap-2 justify-end pt-2">
        <button
          type="button"
          onClick={onCancel}
          className="gray-button"
          disabled={isSaving}
        >
          Cancel
        </button>
        <button
          type="submit"
          className="black-button"
          disabled={isSaving || nothingSelected || boundsInverted}
        >
          {isSaving ? "Saving..." : "Save"}
        </button>
      </div>
    </form>
  );
}
